import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { apiPOST } from "../../utilities/apiHelpers";
import image from "../../../src/assest/image/LoginImage.png"

const Signup = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNo: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

  /* -------------------- Handlers -------------------- */

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  /* -------------------- Validation -------------------- */

  const validate = () => {
    const newErrors = {};

    // Name
    if (!formData.name.trim()) {
      newErrors.name = "Full name is required";
    } else if (!/^[a-zA-Z .'-]+$/.test(formData.name)) {
      newErrors.name = "Full name is invalid";
    }

    // Email
    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email address is invalid";
    }

    // Phone
    if (!formData.phoneNo.trim()) {
      newErrors.phoneNo = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phoneNo)) {
      newErrors.phoneNo = "Phone number must be exactly 10 digits";
    }

    // Password
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = "Must contain one uppercase letter";
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = "Must contain one lowercase letter";
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = "Must contain one number";
    } else if (!/[\W_]/.test(formData.password)) {
      newErrors.password = "Must contain one special character";
    }

    return newErrors;
  };

  /* -------------------- Submit -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Optional: Toast a warning if the user misses fields
      // Toast.fire({
      //   icon: "warning",
      //   title: "Please fix the errors in the form",
      // });
      return;
    }
  
    setErrors({});
    setLoading(true);
  
    try {
      const payload = { ...formData };
      const response = await apiPOST("/v1/auth/signup", payload);
  
      // Standard check for 201 Created or 200 OK based on your API
      if (response?.status === 201 || response?.data?.status) {
        Swal.fire({
          title: "Success!",
          text: "Signup successful! OTP has been sent to your email.",
          icon: "success",
          confirmButtonColor: "#095D7E",
        }).then(() => {
          navigate("/VerifyOTP", { state: formData });
        });
      } else {
        // API returned an error status (e.g., 400) but didn't throw an exception
        Toast.fire({
          icon: "error",
          title: response?.data?.data,
        });
      }
    }catch (error) {
      // 1. Extract the most specific message available
      const errorMessage = 
        error?.response?.data?.data || // Message from your Backend (e.g., "Email already exists")
        error?.response?.data?.error ||   // Some APIs use 'error' key instead of 'message'
        error?.message ||                 // Axios error (e.g., "Network Error")
        "An unexpected error occurred";   // Fallback
  
      // 2. Fire the Toast
      Toast.fire({
        icon: "error",
        title: errorMessage,
      });
  
      console.error("Signup Submission Error:", error);
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- UI -------------------- */

  return (
    <div className="flex max-w-4xl md:mx-auto mt-20 border rounded-lg overflow-hidden shadow-lg mx-5">
      {/* Left Section */}
      {/* <div
        className="hidden md:block flex-1 p-10 rounded-xl m-2"
        style={{ backgroundColor: "#F1F9FF" }}
      >
        
        <div className="mt-[100%]">
          <h2 className="text-2xl font-bold mb-4">Medicines, Home Delivered</h2>
          <p className="text-xs text-gray-400">
            Order any medicine or health product and get free delivery with
            exclusive discounts.
          </p>
        </div>
      </div> */}
      <div style={{ backgroundColor: '#F1F9FF' }} className="hidden sm:hidden flex-1 p-10 flex-col flex-wrap md:block lg:block rounded-xl m-2 justify-end">
         <div className="flex justify-center mb-6 mr-10">
          <img 
            src= {image}
            alt="Healthcare Products"
            className="w-80 h-auto object-contain drop-shadow-xl"
          />
        </div>
        <div className='mt-10'>
          <h2 className="text-2xl font-bold mb-4">Medicines, Home Delivered</h2>
          <p className="text-xs text-gray-400">
            Order any medicine or health product and get free delivery with
            exclusive discounts.
          </p>
        </div>
      </div>
      

      {/* Right Section */}
      <div className="flex-1 p-10 bg-white">
        <h2 className="text-2xl font-bold mb-2">Sign up</h2>
        <p className="text-gray-600 text-xs mb-6">
          Access orders, lab tests & doctor consultations
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Name */}
          <label className="mb-4">
            <span className="block text-xs mb-2">Full name</span>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter full name"
              className="w-full p-3 rounded-[8px] text-xs bg-[#F8F8F8] outline-none"
            />
            {errors.name && (
              <span className="text-red-500 text-xs">{errors.name}</span>
            )}
          </label>

          {/* Email */}
          <label className="mb-4">
            <span className="block text-xs mb-2">Email address</span>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="off"
              placeholder="Enter email"
              className="w-full p-3 rounded-[8px] text-xs bg-[#F8F8F8] outline-none"
            />
            {errors.email && (
              <span className="text-red-500 text-xs">{errors.email}</span>
            )}
          </label>

          {/* Phone */}
          <label className="mb-4">
            <span className="block text-xs mb-2">Phone number</span>
            <input
              type="text"
              name="phoneNo"
              value={formData.phoneNo}
              onChange={handleChange}
              placeholder="Enter phone number"
              className="w-full p-3 rounded-[8px] text-xs bg-[#F8F8F8] outline-none"
            />
            {errors.phoneNo && (
              <span className="text-red-500 text-xs">{errors.phoneNo}</span>
            )}
          </label>

          {/* Password */}
          <label className="mb-4">
            <span className="block text-xs mb-2">Password</span>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                autoComplete="off"
                placeholder="Enter password"
                className="w-full p-3 rounded-[8px] text-xs bg-[#F8F8F8] outline-none"
              />
              <span
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-600"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            {errors.password && (
              <span className="text-red-500 text-xs">{errors.password}</span>
            )}
          </label>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 text-sm text-white rounded-full ${
              loading ? "bg-gray-400 cursor-not-allowed" : "bg-[#095D7E]"
            }`}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </form>

        <p className="mt-4 text-xs text-gray-600">
          Already have an account?{" "}
          <Link to="/login" className="text-[#095D7E]">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
